'use client';

import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import { saveDailyResponse } from '@/lib/services/dailyResponseService';
import { createClient } from '@/lib/supabase/client';

/* ─────────────────────────────────────────────
   DATA — DAY 02
───────────────────────────────────────────── */
interface Scenario {
  id: number;
  context: string;
  icon: string;
  reflection: string[];
  example: string;
  emotionalResponse: string;
  insight: string;
  action: string;
}

const scenarios: Scenario[] = [
  {
    id: 1,
    context: 'Family Tensions',
    icon: 'HomeIcon',
    reflection: [
      'Who in my family do I feel unresolved tension with?',
      'What incident created this distance?',
      'Have I ever expressed how I truly feel?',
    ],
    example: 'A sibling who received more attention growing up, or a parent whose words still sting years later',
    emotionalResponse: 'Resentment, jealousy, longing, unspoken grief',
    insight:
      'Family wounds are often the deepest because they involve people we expected to love us unconditionally. Naming the pain is the first act of healing.',
    action:
      'Write in your journal: "The family relationship that holds the most unresolved pain for me is ___. The emotion I carry is ___."',
  },
  {
    id: 2,
    context: 'Workplace Rivals',
    icon: 'BriefcaseIcon',
    reflection: [
      'Is there a colleague I avoid or feel competitive with?',
      'What did they do that I have not forgiven?',
      'How much mental energy do I spend on this person?',
    ],
    example: 'A coworker who took credit for your idea or was promoted over you unfairly',
    emotionalResponse: 'Bitterness, envy, distrust, professional insecurity',
    insight:
      'Workplace hostility drains your creativity and focus. Recognizing who occupies your mental space helps you reclaim that energy.',
    action:
      'Write in your journal: "At work, I feel hostility toward ___ because ___. This costs me ___ in energy and peace."',
  },
  {
    id: 3,
    context: 'Romantic Wounds',
    icon: 'HeartIcon',
    reflection: [
      'Is there a past or present partner I still carry anger toward?',
      'What did they do that felt like a betrayal?',
      'Am I projecting past wounds onto current relationships?',
    ],
    example: 'An ex-partner who was unfaithful, or a current partner whose behavior reopens old wounds',
    emotionalResponse: 'Betrayal, anger, grief, guardedness',
    insight:
      'Romantic wounds often shape how we love in the future. Identifying them prevents us from punishing new partners for old pain.',
    action:
      'Write in your journal: "The romantic wound I still carry is ___. The person involved is ___. What I need to release is ___."',
  },
  {
    id: 4,
    context: 'Social & Community Conflicts',
    icon: 'UsersIcon',
    reflection: [
      'Is there someone in my community or social circle I avoid?',
      'What happened between us?',
      'How has this affected my sense of belonging?',
    ],
    example: 'A neighbor dispute, a falling out with a friend group, or a community leader who wronged you',
    emotionalResponse: 'Exclusion, anger, social anxiety, distrust of community',
    insight:
      'Social conflicts can make us feel unsafe in spaces meant for connection. Naming them helps us decide what to repair and what to release.',
    action:
      'Write in your journal: "In my community, I feel disconnected from ___ because ___. What I wish had happened differently is ___."',
  },
  {
    id: 5,
    context: 'Self as Enemy',
    icon: 'ExclamationCircleIcon',
    reflection: [
      'In what ways am I my own worst enemy?',
      'What self-critical thoughts do I repeat most often?',
      'What past mistake do I refuse to forgive myself for?',
    ],
    example: 'Constantly replaying a failure, sabotaging opportunities out of fear, or speaking harshly to yourself',
    emotionalResponse: 'Shame, guilt, self-loathing, perfectionism',
    insight:
      'The most persistent enemy is often the inner critic. Forgiving yourself is not weakness — it is the foundation of all other forgiveness.',
    action:
      'Write in your journal: "The way I am my own enemy is ___. The self-criticism I most need to release is ___."',
  },
];

interface Ritual {
  time: string;
  title: string;
  duration: string;
  description: string;
  steps: string[];
  color: string;
  bgColor: string;
  borderColor: string;
  iconBg: string;
  emoji: string;
}

const rituals: Ritual[] = [
  {
    time: 'Morning',
    title: 'Naming & Awareness',
    duration: '5–7 min',
    description:
      'Begin your day by honestly naming the people or situations where you carry unresolved hostility. Awareness without judgment is the first step to freedom.',
    steps: [
      'Take 3 slow breaths and arrive in the present moment',
      'Ask yourself: "Who or what am I carrying resentment toward today?"',
      'Name them silently — without judgment, without justification',
      'Set this intention: "Today I choose to see clearly, not to condemn."',
    ],
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    iconBg: 'bg-amber-100',
    emoji: '🌅',
  },
  {
    time: 'Daytime',
    title: 'Trigger Observation',
    duration: '5–7 min',
    description:
      'During your active hours, notice when hostility or resentment arises in real-time. Observe without reacting — this is the practice of conscious awareness.',
    steps: [
      'Pause when you notice irritation, avoidance, or resentment',
      'Ask: "Who is this really about — this person, or a pattern from my past?"',
      'Breathe and observe the emotion without acting on it',
      'Note mentally: "I noticed ___ today. I will reflect on this tonight."',
    ],
    color: 'text-sky-700',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-200',
    iconBg: 'bg-sky-100',
    emoji: '☀️',
  },
  {
    time: 'Midday',
    title: 'Compassion Pause',
    duration: '2–3 min',
    description:
      'A brief reset to soften the edges of any hostility you have noticed. Compassion does not excuse harm — it frees you from being consumed by it.',
    steps: [
      'Close your eyes and take one deep breath',
      'Think of one person you identified as an "enemy" today',
      'Ask: "What pain might they be carrying that I cannot see?"',
    ],
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    iconBg: 'bg-emerald-100',
    emoji: '🤝',
  },
  {
    time: 'Evening',
    title: 'Enemy Mapping + Release',
    duration: '7–10 min',
    description:
      'Close your day by honestly mapping the relationships where hostility lives — and beginning the process of releasing their hold on you.',
    steps: [
      'Open your journal and write: "Today I noticed hostility toward ___"',
      'For each person or situation, write one sentence about what they triggered',
      'Ask yourself: "What does this tell me about what I value or fear?"',
      'Write one sentence of release: "I choose to carry this lightly tonight."',
      'End with one breath of gratitude for your own courage to look honestly',
    ],
    color: 'text-violet-700',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
    iconBg: 'bg-violet-100',
    emoji: '🌙',
  },
];

/* ─────────────────────────────────────────────
   JOURNAL MODAL
───────────────────────────────────────────── */
interface JournalModalProps {
  title: string;
  promptText: string;
  onClose: () => void;
  onSave: (entry: string, name: string) => Promise<void>;
  saving: boolean;
  saved: boolean;
}

function JournalModal({ title, promptText, onClose, onSave, saving, saved }: JournalModalProps) {
  const [entry, setEntry] = useState('');
  const [name, setName] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-r from-primary to-secondary px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <Icon name="PencilSquareIcon" size={18} className="text-white" />
            </div>
            <h3 className="text-white font-headline font-bold text-base">{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <Icon name="XMarkIcon" size={16} className="text-white" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-primary/8 border border-primary/20 rounded-xl p-4">
            <p className="text-sm text-foreground font-body italic leading-relaxed">{promptText}</p>
          </div>

          <div>
            <label className="block text-sm font-cta font-bold text-foreground mb-1.5">
              Your Journal Entry <span className="text-red-500">*</span>
            </label>
            <textarea
              value={entry}
              onChange={(e) => setEntry(e.target.value)}
              rows={5}
              placeholder="Write your honest reflection here…"
              className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-cta font-bold text-foreground mb-1.5">
              Your Name <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Leave blank to submit as Anonymous"
              className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition"
            />
          </div>

          {saved && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
              <Icon name="CheckCircleIcon" size={18} className="text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-700 font-body font-medium">
                Your response has been saved! Keep going — you are doing great.
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button
              onClick={() => onSave(entry, name)}
              disabled={saving || !entry.trim()}
              className="flex-1 px-6 py-2.5 bg-primary text-primary-foreground rounded-xl font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Icon name="CheckIcon" size={16} className="text-primary-foreground" />
                  Save Response
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="px-5 py-2.5 border border-border text-foreground rounded-xl font-cta text-sm font-bold transition-all duration-300 hover:bg-muted"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const DayTwoExperienceSection = () => {
  const DAY_NUMBER = 2;
  const DAY_TITLE = 'Identifying Your Enemies';

  const [sectionOpen, setSectionOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'scenarios' | 'rituals'>('scenarios');
  const [openScenario, setOpenScenario] = useState<number | null>(null);
  const [expandedRitual, setExpandedRitual] = useState<number | null>(null);

  const [journalModal, setJournalModal] = useState<{
    open: boolean;
    title: string;
    promptText: string;
    responseType: 'scenario' | 'ritual';
    scenarioId?: number;
    scenarioContext?: string;
    ritualTime?: string;
  } | null>(null);
  const [journalSaving, setJournalSaving] = useState(false);
  const [journalSaved, setJournalSaved] = useState(false);

  const [completedScenarios, setCompletedScenarios] = useState<Set<number>>(new Set());
  const [completedRituals, setCompletedRituals] = useState<Set<string>>(new Set());

  // ── Day 2 unlock state (EAT midnight logic) ──
  const [isDay2Unlocked, setIsDay2Unlocked] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [unlockCountdown, setUnlockCountdown] = useState('');
  // Day status: 'today' | 'past' | 'unknown'
  const [dayStatus, setDayStatus] = useState<'today' | 'past' | 'unknown'>('unknown');

  /** Returns current date/time in Dar es Salaam (EAT = UTC+3) */
  const getEATNow = (): Date => {
    const now = new Date();
    const eatOffsetMs = 3 * 60 * 60 * 1000;
    return new Date(now.getTime() + eatOffsetMs - now.getTimezoneOffset() * 60 * 1000);
  };

  /** Returns date string YYYY-MM-DD in EAT */
  const getEATDateString = (date?: Date): string => {
    const d = date || getEATNow();
    const year = d.getUTCFullYear();
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  /** Returns ms until next midnight EAT */
  const msUntilEATMidnight = (): number => {
    const eatNow = getEATNow();
    const nextMidnightEAT = new Date(
      Date.UTC(eatNow.getUTCFullYear(), eatNow.getUTCMonth(), eatNow.getUTCDate() + 1, 0, 0, 0)
    );
    const eatOffsetMs = 3 * 60 * 60 * 1000;
    const nowUTC = new Date();
    return nextMidnightEAT.getTime() - eatOffsetMs - nowUTC.getTime() + nowUTC.getTimezoneOffset() * 60 * 1000;
  };

  /** Format ms as HH:MM:SS countdown */
  const formatCountdown = (ms: number): string => {
    if (ms <= 0) return '00:00:00';
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  /** Check if Day 2 is unlocked based on journey start date */
  const checkDay2Unlocked = (startDateStr: string): boolean => {
    const todayStr = getEATDateString();
    const start = new Date(startDateStr + 'T00:00:00Z');
    const today = new Date(todayStr + 'T00:00:00Z');
    const elapsed = Math.floor((today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    // Day 2 unlocks when elapsed >= 1 (i.e., at least one midnight has passed since start)
    return elapsed >= 1;
  };

  useEffect(() => {
    setIsHydrated(true);

    const loadUnlockState = async () => {
      try {
        const savedEmail = typeof window !== 'undefined' ? localStorage.getItem('calendar_email') : null;
        if (savedEmail) {
          const supabase = createClient();
          const { data } = await supabase
            .from('calendar_progress')
            .select('journey_start_date')
            .eq('email', savedEmail)
            .maybeSingle();

          if (data?.journey_start_date) {
            setIsDay2Unlocked(checkDay2Unlocked(data.journey_start_date));
            // Compute day status: elapsed === 1 means today is Day 2
            const todayStr = getEATDateString();
            const start = new Date(data.journey_start_date + 'T00:00:00Z');
            const today = new Date(todayStr + 'T00:00:00Z');
            const elapsed = Math.max(0, Math.floor((today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
            setDayStatus(elapsed === 1 ? 'today' : elapsed > 1 ? 'past' : 'unknown');
            return;
          }
        }
        // No journey started yet — Day 2 is locked
        setIsDay2Unlocked(false);
      } catch {
        setIsDay2Unlocked(false);
      }
    };

    loadUnlockState();
  }, []);

  // Countdown timer — updates every second until midnight EAT
  useEffect(() => {
    if (isDay2Unlocked) return;

    const tick = () => {
      const ms = msUntilEATMidnight();
      setUnlockCountdown(formatCountdown(ms));
      if (ms <= 0) {
        setIsDay2Unlocked(true);
      }
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isDay2Unlocked]);

  // Schedule auto-unlock at next EAT midnight
  useEffect(() => {
    if (isDay2Unlocked) return;
    const ms = msUntilEATMidnight();
    const timeout = setTimeout(() => {
      setIsDay2Unlocked(true);
    }, ms);
    return () => clearTimeout(timeout);
  }, [isDay2Unlocked]);

  const scenariosRef = useRef<HTMLDivElement>(null);
  const ritualsRef = useRef<HTMLDivElement>(null);
  const scenarioRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const ritualRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      if (!hash) return;

      const ritualMatch = hash.match(/^#day2-ritual-(.+)$/);
      if (ritualMatch) {
        const ritualTime = decodeURIComponent(ritualMatch[1]);
        setSectionOpen(true);
        setActiveTab('rituals');
        const ritualIndex = rituals.findIndex((r) => r.time === ritualTime);
        if (ritualIndex !== -1) setExpandedRitual(ritualIndex);
        setTimeout(() => {
          ritualRefs.current[ritualTime]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 350);
        return;
      }

      const scenarioMatch = hash.match(/^#day2-scenario-(\d+)$/);
      if (scenarioMatch) {
        const scenarioId = parseInt(scenarioMatch[1]);
        setSectionOpen(true);
        setActiveTab('scenarios');
        setOpenScenario(scenarioId);
        setTimeout(() => {
          scenarioRefs.current[scenarioId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 350);
        return;
      }

      if (hash === '#day2-activities') {
        setSectionOpen(true);
        setActiveTab('scenarios');
        setTimeout(() => scenariosRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 350);
      } else if (hash === '#day2-rituals') {
        setSectionOpen(true);
        setActiveTab('rituals');
        setTimeout(() => ritualsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 350);
      }
    };

    handleHash();
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);

  const openJournalForScenario = (scenario: Scenario) => {
    setJournalSaved(false);
    setJournalModal({
      open: true,
      title: `Day ${DAY_NUMBER} · ${scenario.context}`,
      promptText: scenario.action,
      responseType: 'scenario',
      scenarioId: scenario.id,
      scenarioContext: scenario.context,
    });
  };

  const openJournalForRitual = (ritual: Ritual) => {
    setJournalSaved(false);
    setJournalModal({
      open: true,
      title: `${ritual.emoji} ${ritual.time} Ritual · ${ritual.title}`,
      promptText: `Reflect on your ${ritual.time.toLowerCase()} practice: ${ritual.description}`,
      responseType: 'ritual',
      ritualTime: ritual.time,
    });
  };

  const handleJournalSave = async (entry: string, name: string) => {
    if (!journalModal || !entry.trim()) return;
    setJournalSaving(true);
    const { error } = await saveDailyResponse({
      day_number: DAY_NUMBER,
      day_title: DAY_TITLE,
      response_type: journalModal.responseType,
      scenario_id: journalModal.scenarioId ?? null,
      scenario_context: journalModal.scenarioContext ?? null,
      ritual_time: journalModal.ritualTime ?? null,
      journal_entry: entry.trim(),
      completed: true,
      participant_name: name.trim() || 'Anonymous',
    });
    setJournalSaving(false);
    if (!error) {
      setJournalSaved(true);
      if (journalModal.responseType === 'scenario' && journalModal.scenarioId) {
        setCompletedScenarios((prev) => new Set(prev).add(journalModal.scenarioId!));
      } else if (journalModal.responseType === 'ritual' && journalModal.ritualTime) {
        setCompletedRituals((prev) => new Set(prev).add(journalModal.ritualTime!));
      }
      setTimeout(() => setJournalModal(null), 2000);
    }
  };

  const totalCompleted = completedScenarios.size + completedRituals.size;
  const totalItems = scenarios.length + rituals.length;

  // ── Not hydrated yet — render nothing to avoid SSR mismatch ──
  if (!isHydrated) return null;

  // ── Day 2 is locked — show a locked placeholder ──
  if (!isDay2Unlocked) {
    return null;
  }

  return (
    <section className="py-8 lg:py-12 bg-background" id="day2-experience">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* ── Clickable Section Header ── */}
        <button
          onClick={() => setSectionOpen(!sectionOpen)}
          className="w-full text-left group focus:outline-none"
          aria-expanded={sectionOpen}
        >
          <div className={`rounded-2xl border-2 transition-all duration-300 p-8 ${sectionOpen ? 'border-secondary bg-secondary/5' : 'border-secondary/30 bg-gradient-to-br from-secondary/8 to-primary/8 hover:border-secondary hover:bg-secondary/10'}`}>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-5">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${sectionOpen ? 'bg-secondary' : 'bg-secondary/20 group-hover:bg-secondary'}`}>
                  <Icon name="EyeIcon" size={30} className={`transition-colors duration-300 ${sectionOpen ? 'text-white' : 'text-secondary group-hover:text-white'}`} />
                </div>
                <div>
                  <div className="inline-flex items-center gap-2 mb-1">
                    <span className="text-xs font-cta font-bold text-secondary uppercase tracking-widest">Week 1 · Self-Awareness</span>
                    {dayStatus === 'today' && (
                      <span className="text-xs font-cta font-bold text-white bg-secondary rounded-full px-2 py-0.5">Today</span>
                    )}
                    {dayStatus === 'past' && (
                      <span className="text-xs font-cta font-bold text-white bg-green-600 rounded-full px-2 py-0.5">✓ Completed</span>
                    )}
                  </div>
                  <h2 className="text-2xl lg:text-4xl font-headline font-bold text-secondary leading-tight">
                    Day 2: Identifying Your Enemies
                  </h2>
                  <p className="text-sm text-muted-foreground font-body mt-1">
                    5 life scenarios · 4 daily rituals · Journaling actions · ~45 min total
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                {totalCompleted > 0 && (
                  <span className="text-xs font-cta font-bold text-green-600 bg-green-50 border border-green-200 rounded-full px-3 py-1">
                    {totalCompleted}/{totalItems} completed
                  </span>
                )}
                <div className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${sectionOpen ? 'bg-secondary border-secondary' : 'border-secondary/40 group-hover:border-secondary group-hover:bg-secondary/10'}`}>
                  <Icon
                    name={sectionOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                    size={22}
                    className={`transition-colors duration-300 ${sectionOpen ? 'text-white' : 'text-secondary'}`}
                  />
                </div>
              </div>
            </div>

            {!sectionOpen && (
              <div className="mt-5 flex flex-wrap gap-3 items-center">
                <span className="text-sm text-muted-foreground font-body">Includes:</span>
                {['Family Tensions', 'Workplace Rivals', 'Romantic Wounds', 'Social Conflicts', 'Self as Enemy'].map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-secondary/10 text-secondary rounded-full text-xs font-cta font-bold border border-secondary/20">
                    {tag}
                  </span>
                ))}
                {['🌅 Morning', '☀️ Daytime', '🤝 Midday', '🌙 Evening'].map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-cta font-bold border border-amber-200">
                    {tag}
                  </span>
                ))}
                <span className="ml-auto text-sm font-cta font-bold text-secondary flex items-center gap-1">
                  <Icon name="HandPointerIcon" size={16} className="text-secondary" />
                  Click to explore
                </span>
              </div>
            )}
          </div>
        </button>

        {/* ── Expandable Content ── */}
        {sectionOpen && (
          <div className="mt-8 animate-in fade-in slide-in-from-top-2 duration-300">

            {/* Objective */}
            <div className="text-center mb-8">
              <p className="text-lg text-muted-foreground font-body max-w-3xl mx-auto">
                <strong>Objective:</strong> Identify the relationships in your life where hostility, resentment, or unresolved conflict exists. Naming your "enemies" is not about blame — it is about honest self-awareness.
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-3">
                {['Awareness', 'Honesty', 'Naming Pain', 'Self-Reflection'].map((tag) => (
                  <span key={tag} className="px-4 py-1 bg-secondary/10 text-secondary rounded-full text-sm font-cta font-bold border border-secondary/20">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Progress bar */}
            {totalCompleted > 0 && (
              <div className="mb-8 bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-cta font-bold text-green-700">Today's Progress</span>
                  <span className="text-sm font-cta font-bold text-green-700">{totalCompleted} / {totalItems} activities</span>
                </div>
                <div className="w-full bg-green-100 rounded-full h-2.5">
                  <div
                    className="bg-green-500 h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${(totalCompleted / totalItems) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* ── Tab Navigation ── */}
            <div className="flex gap-2 mb-8 bg-muted/40 rounded-2xl p-1.5 border border-border">
              <button
                id="day2-activities"
                onClick={() => setActiveTab('scenarios')}
                className={`flex-1 flex items-center justify-center gap-2.5 py-3 px-4 rounded-xl font-cta text-sm font-bold transition-all duration-200 ${
                  activeTab === 'scenarios' ? 'bg-white text-secondary shadow-sm border border-secondary/20' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="BookOpenIcon" size={18} className={activeTab === 'scenarios' ? 'text-secondary' : 'text-muted-foreground'} />
                <span>Daily Activities</span>
                <span className={`text-xs rounded-full px-2 py-0.5 font-bold ${activeTab === 'scenarios' ? 'bg-secondary/10 text-secondary' : 'bg-muted text-muted-foreground'}`}>
                  {completedScenarios.size}/{scenarios.length}
                </span>
              </button>
              <button
                id="day2-rituals"
                onClick={() => setActiveTab('rituals')}
                className={`flex-1 flex items-center justify-center gap-2.5 py-3 px-4 rounded-xl font-cta text-sm font-bold transition-all duration-200 ${
                  activeTab === 'rituals' ? 'bg-white text-amber-700 shadow-sm border border-amber-200' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <span className="text-base">✨</span>
                <span>Daily Ritual Practices</span>
                <span className={`text-xs rounded-full px-2 py-0.5 font-bold ${activeTab === 'rituals' ? 'bg-amber-50 text-amber-700' : 'bg-muted text-muted-foreground'}`}>
                  {completedRituals.size}/{rituals.length}
                </span>
              </button>
            </div>

            {/* ══════════════════════════════════════
                TAB: DAILY ACTIVITIES (SCENARIOS)
            ══════════════════════════════════════ */}
            {activeTab === 'scenarios' && (
              <div ref={scenariosRef}>
                <div className="bg-gradient-to-br from-secondary/10 to-primary/10 rounded-xl p-6 mb-8 border border-secondary/20">
                  <div className="flex flex-col lg:flex-row gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-secondary rounded-full flex items-center justify-center">
                      <Icon name="LightBulbIcon" size={24} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-headline font-bold text-secondary mb-2">How to Use This Guide</h3>
                      <p className="text-sm text-foreground font-body leading-relaxed">
                        Reflect on each life area below. For each scenario, consider who or what triggers negative emotions in you. Write down names or situations without judgment — this is a private, safe exercise. Then click <strong>"Record My Response"</strong> to save your journal entry.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-5">
                  {scenarios.map((scenario) => {
                    const isDone = completedScenarios.has(scenario.id);
                    return (
                      <div
                        key={scenario.id}
                        id={`day2-scenario-${scenario.id}`}
                        ref={(el) => { scenarioRefs.current[scenario.id] = el; }}
                        className={`bg-card rounded-xl shadow-md border overflow-hidden transition-all duration-200 ${isDone ? 'border-green-300 bg-green-50/30' : 'border-border'}`}
                      >
                        <button
                          onClick={() => setOpenScenario(openScenario === scenario.id ? null : scenario.id)}
                          className="w-full flex items-center justify-between p-6 text-left hover:bg-muted/30 transition-colors duration-200"
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${isDone ? 'bg-green-100' : 'bg-secondary/10'}`}>
                              {isDone ? (
                                <Icon name="CheckCircleIcon" size={24} className="text-green-600" />
                              ) : (
                                <Icon name={scenario.icon as any} size={24} className="text-secondary" />
                              )}
                            </div>
                            <div>
                              <span className="text-xs font-cta font-bold text-muted-foreground uppercase tracking-wide">
                                Scenario {scenario.id} {isDone && '· ✓ Completed'}
                              </span>
                              <h3 className="text-lg lg:text-xl font-headline font-bold text-secondary">
                                {scenario.context}
                              </h3>
                            </div>
                          </div>
                          <Icon
                            name={openScenario === scenario.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                            size={22}
                            className="text-muted-foreground flex-shrink-0"
                          />
                        </button>

                        {openScenario === scenario.id && (
                          <div className="px-6 pb-8 border-t border-border">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-6">
                              <div className="bg-muted/40 rounded-lg p-5">
                                <div className="flex items-center gap-2 mb-3">
                                  <Icon name="QuestionMarkCircleIcon" size={20} className="text-secondary" />
                                  <h4 className="text-sm font-cta font-bold text-secondary uppercase tracking-wide">Reflection Questions</h4>
                                </div>
                                <ul className="space-y-2">
                                  {scenario.reflection.map((q, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm text-foreground font-body">
                                      <span className="text-secondary font-bold mt-0.5">•</span>
                                      <span>{q}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div className="bg-muted/40 rounded-lg p-5">
                                <div className="flex items-center gap-2 mb-3">
                                  <Icon name="DocumentTextIcon" size={20} className="text-secondary" />
                                  <h4 className="text-sm font-cta font-bold text-secondary uppercase tracking-wide">Example Scenario</h4>
                                </div>
                                <p className="text-sm text-foreground font-body italic">"{scenario.example}"</p>
                              </div>
                              <div className="bg-muted/40 rounded-lg p-5">
                                <div className="flex items-center gap-2 mb-3">
                                  <Icon name="FaceSmileIcon" size={20} className="text-secondary" />
                                  <h4 className="text-sm font-cta font-bold text-secondary uppercase tracking-wide">Emotional Response</h4>
                                </div>
                                <p className="text-sm text-foreground font-body">{scenario.emotionalResponse}</p>
                              </div>
                              <div className="bg-muted/40 rounded-lg p-5">
                                <div className="flex items-center gap-2 mb-3">
                                  <Icon name="EyeIcon" size={20} className="text-secondary" />
                                  <h4 className="text-sm font-cta font-bold text-secondary uppercase tracking-wide">Expected Insight</h4>
                                </div>
                                <p className="text-sm text-foreground font-body">{scenario.insight}</p>
                              </div>
                            </div>

                            <div className="mt-6 bg-secondary/10 border border-secondary/30 rounded-lg p-5">
                              <div className="flex items-center gap-2 mb-3">
                                <Icon name="PencilSquareIcon" size={20} className="text-secondary" />
                                <h4 className="text-sm font-cta font-bold text-secondary uppercase tracking-wide">Practical Action — Write in Your Journal</h4>
                              </div>
                              <p className="text-base text-foreground font-body italic font-medium mb-4">
                                {scenario.action}
                              </p>
                              <button
                                onClick={() => openJournalForScenario(scenario)}
                                className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md ${
                                  isDone
                                    ? 'bg-green-100 text-green-700 border border-green-300' :'bg-secondary text-white'
                                }`}
                              >
                                <Icon name={isDone ? 'CheckCircleIcon' : 'PencilSquareIcon'} size={16} className={isDone ? 'text-green-600' : 'text-white'} />
                                {isDone ? 'Response Saved ✓' : 'Record My Response'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ══════════════════════════════════════
                TAB: DAILY RITUAL PRACTICES
            ══════════════════════════════════════ */}
            {activeTab === 'rituals' && (
              <div ref={ritualsRef}>
                <div className="bg-gradient-to-br from-amber-50 to-violet-50 border border-amber-200 rounded-xl p-6 mb-8">
                  <div className="flex flex-col lg:flex-row gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center text-xl">✨</div>
                    <div>
                      <h3 className="text-lg font-headline font-bold text-slate-800 mb-2">Day 2 Ritual Practices</h3>
                      <p className="text-sm text-slate-600 font-body leading-relaxed">
                        Today's rituals are designed to help you name and observe hostility without judgment. Each practice builds your capacity for honest self-awareness — the foundation of all forgiveness. Choose any ritual, expand it to read the steps, then click <strong>"Record My Practice"</strong> to log your reflection.
                      </p>
                      <p className="text-xs text-slate-500 font-body mt-2">
                        <span className="font-semibold">Total daily commitment:</span>{' '}
                        <span className="text-indigo-600 font-medium">~20–27 minutes</span>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {rituals.map((ritual, index) => {
                    const isDone = completedRituals.has(ritual.time);
                    const isExpanded = expandedRitual === index;
                    return (
                      <div
                        key={index}
                        id={`day2-ritual-${ritual.time}`}
                        ref={(el) => { ritualRefs.current[ritual.time] = el; }}
                        className={`rounded-2xl border-2 overflow-hidden transition-all duration-200 ${isDone ? 'border-green-300 bg-green-50/40' : `${ritual.borderColor} ${ritual.bgColor}`}`}
                      >
                        <button
                          onClick={() => setExpandedRitual(isExpanded ? null : index)}
                          className="w-full flex items-center justify-between p-5 text-left hover:opacity-90 transition-opacity duration-200"
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl ${isDone ? 'bg-green-100' : ritual.iconBg}`}>
                              {isDone ? '✅' : ritual.emoji}
                            </div>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-xs font-semibold uppercase tracking-wider ${isDone ? 'text-green-700' : ritual.color}`}>
                                  {ritual.time} {isDone && '· ✓ Completed'}
                                </span>
                                <span className={`text-xs rounded-full px-2 py-0.5 border ${isDone ? 'bg-green-50 text-green-700 border-green-200' : `${ritual.iconBg} ${ritual.color} ${ritual.borderColor}`}`}>
                                  {ritual.duration}
                                </span>
                              </div>
                              <h3 className="text-base font-bold text-slate-800 mt-0.5">{ritual.title}</h3>
                            </div>
                          </div>
                          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''} ${ritual.iconBg}`}>
                            <svg className={`w-4 h-4 ${ritual.color}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </button>

                        {isExpanded && (
                          <div className="px-5 pb-6 border-t border-white/60">
                            <p className="text-slate-600 text-sm mt-4 leading-relaxed mb-4">{ritual.description}</p>
                            <p className={`text-xs font-semibold uppercase tracking-wider ${ritual.color} mb-3`}>How to practice:</p>
                            <div className="space-y-2 mb-6">
                              {ritual.steps.map((step, i) => (
                                <div key={i} className="flex items-start gap-2.5">
                                  <div className={`flex-shrink-0 w-5 h-5 rounded-full ${ritual.iconBg} border ${ritual.borderColor} flex items-center justify-center mt-0.5`}>
                                    <span className={`text-xs font-bold ${ritual.color}`}>{i + 1}</span>
                                  </div>
                                  <p className="text-slate-700 text-sm leading-relaxed">{step}</p>
                                </div>
                              ))}
                            </div>
                            <button
                              onClick={() => openJournalForRitual(ritual)}
                              className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-xl font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md ${
                                isDone
                                  ? 'bg-green-100 text-green-700 border border-green-300'
                                  : `${ritual.iconBg} ${ritual.color} border ${ritual.borderColor}`
                              }`}
                            >
                              <Icon name={isDone ? 'CheckCircleIcon' : 'PencilSquareIcon'} size={16} className={isDone ? 'text-green-600' : ritual.color} />
                              {isDone ? 'Practice Recorded ✓' : 'Record My Practice'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Closing CTA ── */}
            <div className="mt-14 bg-gradient-to-br from-secondary to-primary rounded-xl p-8 text-center text-white">
              <Icon name="StarIcon" size={40} className="text-white/80 mx-auto mb-4" />
              <h3 className="text-2xl font-headline font-bold mb-3">Complete Your Day 2 Practice</h3>
              <p className="text-base font-body text-white/90 max-w-2xl mx-auto mb-6">
                Take 20 minutes today to honestly map the relationships where hostility lives and complete your daily rituals. Naming your enemies — including yourself — is the courageous first step toward living with no enemy.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="/festival-guide#calendar" className="px-8 py-3 bg-white text-secondary rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-lg">
                  View Full 30-Day Calendar →
                </a>
                <a href="/journal-history" className="px-8 py-3 bg-white/20 text-white border border-white/40 rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:bg-white/30">
                  View Journal History
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Journal Modal ── */}
      {journalModal?.open && (
        <JournalModal
          title={journalModal.title}
          promptText={journalModal.promptText}
          onClose={() => setJournalModal(null)}
          onSave={handleJournalSave}
          saving={journalSaving}
          saved={journalSaved}
        />
      )}
    </section>
  );
};

export default DayTwoExperienceSection;
